import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ConsentData, ConsentModalComponent } from '../components/consent-modal/consent-modal.component';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class ConsentFlowService {
  private isPromptOpen = false;

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  async ensureConsentAccepted(): Promise<boolean> {
    try {
      const status = await firstValueFrom(this.apiService.getConsentStatus());
      if (!status.required) {
        return true;
      }

      const result = await this.openConsentModal();
      if (!result) {
        this.snackBar.open('POPIA acknowledgement was not completed', 'Close', { duration: 5000 });
        return false;
      }

      await firstValueFrom(
        this.apiService.submitConsent({
          action: result.action,
          selectedGrounds: result.selectedGrounds || [],
        })
      );
      if (result.action !== 'accept') {
        this.snackBar.open(
          'You must accept the POPIA acknowledgement before viewing personal data',
          'Close',
          { duration: 5000 }
        );
        return false;
      }

      const refreshedStatus = await firstValueFrom(this.apiService.getConsentStatus());
      if (refreshedStatus.required) {
        this.snackBar.open('POPIA acknowledgement is still required before viewing data', 'Close', { duration: 5000 });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to process POPIA acknowledgement flow:', error);
      this.snackBar.open('Failed to validate POPIA acknowledgement state', 'Close', { duration: 5000 });
      return false;
    }
  }

  isConsentRequiredError(error: any): boolean {
    const reasonCode = String(error?.error?.error?.reasonCode || '').toUpperCase();
    return reasonCode === 'POPIA_CONSENT_REQUIRED';
  }

  private async openConsentModal(): Promise<ConsentData | undefined> {
    if (this.isPromptOpen) return undefined;
    this.isPromptOpen = true;
    try {
      const dialogRef = this.dialog.open(ConsentModalComponent, {
        width: '560px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        disableClose: true,
        autoFocus: false,
        data: {
          requireLegalGround: true,
          showLegalLinks: true,
        },
      });
      return await firstValueFrom(dialogRef.afterClosed()) as ConsentData | undefined;
    } finally {
      this.isPromptOpen = false;
    }
  }
}
